import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import CONFIG from "@/assets/js/config.js";
import axios from "axios";
import { required, maxLength, requiredIf } from "vuelidate/lib/validators";
import tinycolor from "tinycolor2";
import ValidationService from "@/services/ValidationService";
import Select2Multiple from 'v-select2-multiple-component';

const COLOR_INPUT_EMPTY_SWATCH = "#d0d0d0";

export default {
    name: "TransactionCategoryEdit",
    components: {
        MainLayout,
        Loading,
        Select2Multiple,
    },
    mixins: [ValidationService],
    data() {
        return {
            isLoading: false,
            submitted: false,
            disabled: false,
            is_protected: false,
            categoryData: {
                category_name: "",
                color_code: "",
                category_type: null,
                is_active: null,
            },
            noDataMsg: CONFIG.NO_DATA_EXIST,
            transactionTypesOptions: CONFIG.TRANSACTION_TYPES_OPTIONS,
            CONFIG,
        };
    },
    computed: {
        colorPickerDisplay() {
            if (!this.categoryData || !Object.keys(this.categoryData).length) {
                return "";
            }
            return this.normalizeColorInput(this.categoryData.color_code) || "";
        },
        colorPickerInputValue() {
            return this.colorPickerDisplay || COLOR_INPUT_EMPTY_SWATCH;
        },
        colorCodeInvalid() {
            if (!this.submitted || !this.categoryData || !Object.keys(this.categoryData).length) {
                return false;
            }
            if (this.isProtectedCategory) {
                const trimmed = this.trimString(this.categoryData.color_code);
                return !trimmed || !this.isValidOptionalColor(this.categoryData.color_code);
            }
            return !this.isValidOptionalColor(this.categoryData.color_code);
        },
        isProtectedCategory() {
            return this.is_protected;
        },
    },
    watch: {
        colorPickerInputValue: "syncColorPickerInput",
    },
    mounted() {
        this.isLoading = true;
        this.getDetail();
    },
    validations: {
        categoryData: {
            category_name: { required, maxLength: maxLength(255) },
            category_type: { required },
            is_active: { required },
            color_code: {
                required: requiredIf(function () {
                    return this.isProtectedCategory;
                })
            }
        },
    },
    methods: {
        trimString(value) {
            return value != null ? String(value).trim() : "";
        },
        normalizeColorInput(colorInput) {
            const trimmed = this.trimString(colorInput);
            return trimmed || "";
        },
        isValidOptionalColor(colorInput) {
            const trimmed = this.trimString(colorInput);
            if (!trimmed) return true;

            const standardHexRegex = /^#([0-9A-Fa-f]{3}){1,2}$/;
            if (!standardHexRegex.test(trimmed)) return false;

            const color = tinycolor(trimmed);
            if (!color.isValid()) return false;

            return color.toHexString();
        },
        syncColorPickerInput() {
            this.$nextTick(() => {
                const colorPickerElement = this.$refs.colorPicker;
                const pickerHexValue = this.colorPickerInputValue;
                if (
                    colorPickerElement &&
                    pickerHexValue != null &&
                    colorPickerElement.value !== pickerHexValue
                ) {
                    colorPickerElement.value = pickerHexValue;
                }
            });
        },
        isRegistrationFeeCategory(categoryName) {
            const regName = (this.CONFIG.TRANSACTION_CATEGORY.SCHOOL_REGISTRATION_FEE || "School Registration Fee").trim();
            const targetName = String(categoryName ?? "").trim();

            return targetName === regName;
        },
        isRefundCategory(categoryName) {
            const refundName = (this.CONFIG.TRANSACTION_CATEGORY.REFUND || "Refund").trim();
            const targetName = String(categoryName ?? "").trim();

            return targetName === refundName;
        },
        onColorPicked(event) {
            this.categoryData.color_code = event.target.value;
        },
        goToList() {
            this.$router.push({ name: "transactionCategoryList" });
        },
        getDetail() {
            const categoryId = this.$route.params.id;
            this.isLoading = true;
            axios
                .get(`/transactionCategory/detail/${categoryId}`)
                .then((response) => {
                    if (response.data) {
                        const detail = response.data;
                        this.is_protected = !this.isEditAbleCategory(detail.category_name);
                        const colorCodeRaw =
                            detail.color_code != null && this.trimString(detail.color_code)
                                ? this.trimString(detail.color_code)
                                : "";
                        this.categoryData = {
                            category_name:
                                detail.category_name != null ? String(detail.category_name) : "",
                            color_code: this.normalizeColorInput(colorCodeRaw) || "",
                            category_type:
                                detail.category_type != null ? String(detail.category_type) : null,
                            is_active: Number(detail.is_active) === 1 ? 1 : 0,
                        };
                        this.$nextTick(this.syncColorPickerInput);
                    }
                    this.isLoading = false;
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.categoryData = {};
                    if (error.response?.status == 401) {
                        this.$store.commit("logout");
                        window.location.replace("login");
                    }
                    console.error("Error fetching transaction category info:", error);
                });
        },
        submitUpdate() {
            if (!this.categoryData || !Object.keys(this.categoryData).length) {
                return;
            }
            const categoryId = this.$route.params.id;
            this.submitted = true;
            this.disabled = true;
            this.$v.$touch();
            if (this.$v.$invalid || this.colorCodeInvalid) {
                this.disabled = false;
                return;
            }
            this.isLoading = true;
            const payload = {
                category_name: (this.categoryData.category_name || "").trim(),
                color_code: this.normalizeColorInput(this.categoryData.color_code) || null,
                category_type: Number(this.categoryData.category_type),
                is_active: Number(this.categoryData.is_active) === 1,
            };
            axios
                .post("/transactionCategory/update/" + categoryId, payload)
                .then((response) => {
                    if (response.data.success == true) {
                        this.isLoading = false;
                        this.$notification.success(response.data.message, {
                            timer: 3,
                            position: "bottomCenter"
                        });
                        this.$router.push({ name: "transactionCategoryList" });
                    }
                })
                .catch(error => {
                    this.disabled = false;
                    this.clearValidationErrors();
                    if (error.response?.status == 401) {
                        this.$store.commit('logout');
                        window.location.replace('login');
                    } else if (error.response?.status == 422) {
                        this.printValidationError(error.response);
                    }
                    this.isLoading = false;
                })
        },

        isEditAbleCategory(name) {
            return !Object.values(this.CONFIG.TRANSACTION_CATEGORY).includes(name);
        },
    },
};
